import { LabelRedwoodTheme } from '#UNSAFE_Label/themes/redwood/LabelTheme';
import { LabelValueLayout } from '#UNSAFE_LabelValueLayout';
import { SkeletonRedwoodTheme } from '#UNSAFE_Skeleton/themes/redwood/SkeletonTheme';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useTestId, type TestIdProps } from '#hooks/UNSAFE_useTestId';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { classNames } from '#utils/UNSAFE_classNames';
import { Size } from '#utils/UNSAFE_size';
import { LayoutColumnSpan, layoutSpanStyles } from '#utils/UNSAFE_styles/Layout';
import { StyledComponentProps } from '@oracle/oraclejet-internal-utilities/typeUtils';
import type { ComponentChildren, ComponentProps, Ref, JSX } from 'preact';
import { forwardRef } from 'preact/compat';
import { ReadonlyTextFieldContent, VariantProps } from './ReadonlyTextFieldContent';
import { FormLayoutRedwoodTheme } from './themes/redwood/FormLayoutTheme';
import { TextFieldRedwoodTheme } from './themes/redwood/TextFieldTheme';

type DivProps = UnpackSignals<JSX.IntrinsicElements['div']>;
type UAProps = {
  compactUserAssistance?: ComponentChildren;
  inlineUserAssistance?: ComponentChildren;
};
type OuterClassNamesProps = {
  outerClassNames?: string;
};

type PickedPropsFromReadonlyTextFieldContent = Pick<
  ComponentProps<typeof ReadonlyTextFieldContent>,
  'mainContent' | 'endContent' | 'startContent'
>;

type StartTopLabelReadonlyTextFieldProps = Omit<DivProps, 'label'> &
  UAProps &
  VariantProps &
  PickedPropsFromReadonlyTextFieldContent &
  OuterClassNamesProps & {
    columnSpan?: LayoutColumnSpan;
    label?: ComponentChildren;
    labelEdge?: 'start' | 'top';
    labelStartWidth?: Size;
    rootRef?: Ref<HTMLDivElement>;
  };

const StartTopLabelReadonlyTextField = forwardRef(
  (
    {
      outerClassNames,
      columnSpan,
      label,
      labelEdge,
      labelStartWidth,
      compactUserAssistance,
      inlineUserAssistance,
      mainContent,
      endContent,
      rootRef,
      variant,
      resize,
      startContent,
      ...props
    }: StartTopLabelReadonlyTextFieldProps,
    ref?: Ref<HTMLDivElement>
  ) => {
    const parentComponentVariant = variant === 'textarea' ? 'textArea' : 'textField';
    return (
      <div {...props} ref={ref} class={outerClassNames}>
        {compactUserAssistance}
        <LabelValueLayout
          columnSpan={columnSpan}
          label={label}
          labelEdge={labelEdge}
          labelStartWidth={labelStartWidth}
          parentComponentVariant={parentComponentVariant}>
          <ReadonlyTextFieldContent
            mainContent={mainContent}
            endContent={endContent}
            rootRef={rootRef}
            startContent={startContent}
            {...(variant ? ({ variant, resize } as VariantProps) : {})}
          />
          {inlineUserAssistance}
        </LabelValueLayout>
      </div>
    );
  }
);

type InsideLabelReadonlyTextFieldProps = Omit<DivProps, 'label'> &
  UAProps &
  VariantProps &
  PickedPropsFromReadonlyTextFieldContent &
  OuterClassNamesProps & {
    label?: ComponentChildren;
    labelText?: string;
    rootRef?: Ref<HTMLDivElement>;
  };
const InsideLabelReadonlyTextField = forwardRef(
  (
    {
      outerClassNames,
      compactUserAssistance,
      inlineUserAssistance,
      labelText,
      mainContent,
      endContent,
      label,
      rootRef,
      variant,
      resize,
      startContent,
      ...props
    }: InsideLabelReadonlyTextFieldProps,
    ref?: Ref<HTMLDivElement>
  ) => {
    return (
      <div {...props} ref={ref} class={outerClassNames}>
        {compactUserAssistance}
        <ReadonlyTextFieldContent
          insideLabel={label}
          labelText={labelText}
          mainContent={mainContent}
          endContent={endContent}
          rootRef={rootRef}
          startContent={startContent}
          {...(variant ? ({ variant, resize } as VariantProps) : {})}
        />
        {inlineUserAssistance}
      </div>
    );
  }
);

export type StyleProps = UAProps &
  VariantProps &
  PickedPropsFromReadonlyTextFieldContent & {
    columnSpan?: LayoutColumnSpan;
    label?: ComponentChildren;
    labelEdge?: 'inside' | 'start' | 'top';
    labelText?: string;
    variant?: 'textarea';
    mainFieldRef?: Ref<HTMLDivElement>;
  };

type Props = TestIdProps & StyledComponentProps<'div', StyleProps>;

export const ReadonlyTextField = forwardRef(
  (
    {
      columnSpan = 1,
      label,
      labelEdge,
      labelText,
      mainContent,
      endContent,
      variant,
      mainFieldRef,
      startContent,
      testId,
      ...props
    }: Props,
    ref?: Ref<HTMLElement>
  ) => {
    const testIdProps = useTestId(testId);
    const { classes, styles } = useComponentTheme(TextFieldRedwoodTheme, {
      readonly: 'isReadonly'
    });
    const { baseTheme: labelBaseTheme } = useComponentTheme(LabelRedwoodTheme);
    const { baseTheme: formLayoutTheme } = useComponentTheme(FormLayoutRedwoodTheme);

    const { formControlBase } = styles;
    // because TextField components reference Skeleton scoped vars, we need to bring in the Skeleton base theme
    // to pick up the scoped var definitions.
    const { baseTheme: skeletonBaseTheme } = useComponentTheme(SkeletonRedwoodTheme);

    const outerClassNames = classNames([
      classes,
      skeletonBaseTheme,
      formControlBase,
      formLayoutTheme,
      labelBaseTheme,
      layoutSpanStyles.layoutSpanColumn[columnSpan]
    ]);

    if (label !== undefined && (labelEdge === 'start' || labelEdge === 'top')) {
      return (
        <StartTopLabelReadonlyTextField
          columnSpan={columnSpan}
          outerClassNames={outerClassNames}
          label={label}
          labelEdge={labelEdge}
          ref={ref as Ref<HTMLDivElement> | undefined}
          mainContent={mainContent}
          endContent={endContent}
          rootRef={mainFieldRef}
          startContent={startContent}
          variant={variant}
          {...props}
          {...testIdProps}></StartTopLabelReadonlyTextField>
      );
    }

    return (
      <InsideLabelReadonlyTextField
        outerClassNames={outerClassNames}
        ref={ref as Ref<HTMLDivElement> | undefined}
        label={label}
        labelText={labelText}
        mainContent={mainContent}
        endContent={endContent}
        rootRef={mainFieldRef}
        startContent={startContent}
        variant={variant}
        {...props}
        {...testIdProps}></InsideLabelReadonlyTextField>
    );
  }
);
